#!/usr/bin/env node
import { Console } from 'node:console';
import { TextDocument } from 'vscode-languageserver-textdocument';
import { ProposedFeatures, TextDocuments, createConnection } from 'vscode-languageserver/node';
import { install as installSourceMapSupport } from 'source-map-support';
import { chatWebviewPlugin } from '@gitlab-org/webview-chat';
import { workflowPluginFactory } from '@gitlab-org/webview-duo-workflow';
import { setupWebviewRuntime } from '@gitlab-org/webview';
import { JsonRpcConnectionTransport } from '@gitlab-org/webview-transport-json-rpc';
import { WebviewPlugin } from '@gitlab-org/webview-plugin';
import { Transport } from '@gitlab-org/webview-transport';
import { duoChatPluginFactory } from '@gitlab-org/webview-duo-chat';
import { Container, brandInstance } from '@gitlab-org/di';
import { ThemeProvider, addWebviewThemeServicesToContainer } from '@gitlab-org/webview-theme';
import { DefaultSecretRedactor } from '../common/secret_redaction';
import { DefaultAIContextManager } from '../common/ai_context_management/ai_context_manager';
import { DefaultOpenTabContextProvider } from '../common/ai_context_management/context_providers/file_open_tab';
import { log } from '../common/log';
import {
  ConfigService,
  DefaultConfigService,
  DefaultDocumentTransformerService,
  DefaultStreamingHandler,
  GitLabAPI,
  GitLabApiClient,
  setup,
} from '../common';
import { LsConnection, LsTextDocuments } from '../common/external_interfaces';
import { LsFetch } from '../common/fetch';
import { DefaultFeatureFlagService, FeatureFlagService } from '../common/feature_flags';
import { SnowplowTracker } from '../common/tracking/snowplow_tracker';
import { getLanguageServerVersion } from '../common/utils/get_language_server_version';
import { MultiTracker } from '../common/tracking/multi_tracker';
import { TelemetryTracker } from '../common/tracking/tracking_types';
import { InstanceTracker } from '../common/tracking/instance_tracker';
import { DefaultErrorHandler } from '../common/errors/error_handler';
import { DefaultAdvancedContextService } from '../common/advanced_context/advanced_context_service';
import { DefaultDocumentService, DocumentService } from '../common/document_service';
import { DefaultTokenCheckNotifier } from '../common/core/handlers/token_check_notifier';
import { DefaultSecurityDiagnosticsPublisher } from '../common/security_diagnostics_publisher';
import { DefaultConnectionService } from '../common/connection_service';
import { WorkflowGraphQLService } from '../common/graphql/workflow/service';
import { DefaultInitializeHandler } from '../common/core/handlers/initialize_handler';
import { DefaultDidChangeWorkspaceFoldersHandler } from '../common/core/handlers/did_change_workspace_folders_handler';
import {
  ExtensionConnectionMessageBusProvider,
  WebviewLocationService,
  WebviewMetadataProvider,
} from '../common/webview';
import { DefaultSupportedLanguagesService } from '../common/suggestion/supported_languages_service';
import {
  DefaultFeatureStateManager,
  DefaultCodeSuggestionsSupportedLanguageCheck,
  DefaultProjectDuoAccessCheck,
  DefaultCodeSuggestionsDuoLicenseCheck,
  DefaultCodeSuggestionsInstanceVersionCheck,
} from '../common/feature_state';
import { DefaultDuoProjectAccessChecker } from '../common/services/duo_access/project_access_checker';
import {
  DefaultDuoProjectAccessCache,
  DuoProjectAccessCache,
} from '../common/services/duo_access/project_access_cache';
import { DefaultSuggestionApiErrorCheck } from '../common/feature_state/suggestion_api_error_check';
import { DeafultPostProcessorPipeline } from '../common/suggestion_client/post_processors/default_post_processor_pipeline';
import { DefaultSuggestionApiErrorNotifier } from '../common/feature_state/suggestion_api_error_notifier';
import { DefaultSuggestionService } from '../common/suggestion/suggestion_service';
import {
  DefaultVirtualFileSystemService,
  VirtualFileSystemService,
} from '../common/services/fs/virtual_file_system_service';
import { DefaultRepositoryService } from '../common/services/git/repository_service';
import { DefaultLocalFileContextProvider } from '../common/ai_context_management/context_providers/file_local_search';
import { DesktopDirectoryWalker, DesktopFileResolver } from './services/fs';
import { DesktopWorkflowRunner } from './duo_workflow/desktop_workflow_runner';
import { DesktopTreeSitterParser } from './tree_sitter/parser';
import { Fetch } from './fetch';
import { setupHttp } from './setup_http';
import { NodeSentryTracker } from './node_sentry_tracker';
import { DesktopFsClient } from './services/fs/fs';

const webviewPlugins = new Set<WebviewPlugin>();
webviewPlugins.add(chatWebviewPlugin);

async function main() {
  const useSourceMaps = process.argv.includes('--use-source-maps');
  const printVersion = process.argv.includes('--version') || process.argv.includes('-v');
  const version = getLanguageServerVersion();
  if (printVersion) {
    // eslint-disable-next-line no-console
    console.error(`GitLab Language Server v${version}`);
    return;
  }

  if (useSourceMaps) installSourceMapSupport();
  const container = new Container();
  container.instantiate(DefaultConfigService, DefaultSupportedLanguagesService);
  const configService = container.get(ConfigService);
  const connection = createConnection(ProposedFeatures.all);
  container.addInstances(brandInstance(LsConnection, connection));
  const documents: TextDocuments<TextDocument> = new TextDocuments(TextDocument);
  container.addInstances(brandInstance(LsTextDocuments, documents));
  log.setup(configService);

  // Send all console messages to stderr. Stdin/stdout may be in use
  // as the LSP communications channel.
  //
  // This has to happen after the `createConnection` because the `vscode-languageserver` server version 9 and higher
  // patches the console as well
  // https://github.com/microsoft/vscode-languageserver-node/blob/84285312d8d9f22ee0042e709db114e5415dbdde/server/src/node/main.ts#L270
  //
  // FIXME: we should really use the remote logging with `window/logMessage` messages
  // That's what the authors of the languageserver-node want us to use
  // https://github.com/microsoft/vscode-languageserver-node/blob/4e057d5d6109eb3fcb075d0f99456f05910fda44/server/src/common/server.ts#L133
  global.console = new Console({ stdout: process.stderr, stderr: process.stderr });
  const lsFetch = new Fetch();
  await lsFetch.initialize();
  container.addInstances(brandInstance(LsFetch, lsFetch));

  const documentService = new DefaultDocumentService(documents);
  container.addInstances(brandInstance(DocumentService, documentService));

  addWebviewThemeServicesToContainer(container, log);
  container.instantiate(
    GitLabAPI,
    DefaultDocumentTransformerService,
    DefaultFeatureFlagService,
    DefaultTokenCheckNotifier,
    DefaultSecretRedactor,
    DefaultSecurityDiagnosticsPublisher,
    DefaultInitializeHandler,
    DefaultDidChangeWorkspaceFoldersHandler,
    DefaultCodeSuggestionsSupportedLanguageCheck,
    DefaultProjectDuoAccessCheck,
    DefaultCodeSuggestionsDuoLicenseCheck,
    DefaultCodeSuggestionsInstanceVersionCheck,
    DefaultAdvancedContextService,
    DesktopFileResolver,
    DesktopDirectoryWalker,
    DefaultDuoProjectAccessChecker,
    DefaultDuoProjectAccessCache,
    DefaultFeatureStateManager,
    DesktopTreeSitterParser,
    DefaultSuggestionApiErrorCheck,
    DefaultAIContextManager,
    DefaultOpenTabContextProvider,
    DeafultPostProcessorPipeline,
    DefaultSuggestionApiErrorNotifier,
    NodeSentryTracker,
    DefaultErrorHandler,
    DefaultVirtualFileSystemService,
    DefaultRepositoryService,
    DesktopFsClient,
    DefaultLocalFileContextProvider,
  );
  const snowplowTracker = new SnowplowTracker(
    container.get(LsFetch),
    container.get(ConfigService),
    container.get(FeatureFlagService),
    container.get(GitLabApiClient),
  );

  const workflowGraphqlAPI = new WorkflowGraphQLService(container.get(GitLabApiClient), log);

  const instanceTracker = new InstanceTracker(
    container.get(GitLabApiClient),
    container.get(ConfigService),
  );

  const telemetryTracker = new MultiTracker([snowplowTracker, instanceTracker]);

  container.addInstances(brandInstance(TelemetryTracker, telemetryTracker));

  // FIXME: move the Telemetry tracker construction to DI so we don't have
  // order-dependent initialization like with stream handler
  container.instantiate(
    DefaultStreamingHandler,
    DefaultConnectionService,
    DefaultSuggestionService,
  );

  const workflowAPI = new DesktopWorkflowRunner(
    configService,
    lsFetch,
    container.get(GitLabApiClient),
  );

  const webviewLocationService = new WebviewLocationService();
  const webviewTransports = new Set<Transport>();
  const webviewMetadataProvider = new WebviewMetadataProvider(
    webviewLocationService,
    webviewPlugins,
  );

  log.info(`GitLab Language Server is starting (v${version})`);

  webviewTransports.add(
    new JsonRpcConnectionTransport({
      connection,
      logger: log,
    }),
  );

  const extensionMessageBusProvider = new ExtensionConnectionMessageBusProvider({
    connection,
    logger: log,
  });
  setup(
    telemetryTracker,
    connection,
    container.get(FeatureFlagService),
    configService,
    webviewMetadataProvider,
    workflowAPI,
    container.get(DuoProjectAccessCache),
    container.get(VirtualFileSystemService),
  );

  // Make the text document manager listen on the connection for open, change and close text document events
  documents.listen(connection);
  // Listen on the connection
  connection.listen();

  webviewPlugins.add(workflowPluginFactory(workflowGraphqlAPI, workflowAPI, connection));
  webviewPlugins.add(
    duoChatPluginFactory({
      gitlabApiClient: container.get(GitLabApiClient),
      logger: log,
    }),
  );

  await setupHttp(Array.from(webviewPlugins), webviewLocationService, webviewTransports, log);

  setupWebviewRuntime({
    extensionMessageBusProvider,
    plugins: Array.from(webviewPlugins),
    transports: Array.from(webviewTransports),
    logger: log,
    themeProvider: container.get(ThemeProvider),
  });

  log.info('GitLab Language Server has started');
}

main().catch((e) => log.error(`failed to start the language server`, e));
